import React from 'react';
import { CheckCircle2, ExternalLink, Instagram } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styles from './InstagramBasicProfileData.module.scss';

const InstagramBasicProfileData = ({ profile, isCreator, canManageConnection, onConnect }) => {
  const { t } = useTranslation('profile');
  const isConnected = Boolean(profile?.instagram_account_connected);

  const username = isCreator ? profile?.ig_account : profile?.instagram_account;
  const normalizedUsername = username?.replace(/^@/, '');
  const profilePictureIsFromInstagram = Boolean(
    profile?.ig_profile_picture_url && !profile.ig_profile_picture_url.includes('ui-avatars.com')
  );
  const websiteUrl = profile?.ig_website;
  const normalizedWebsiteUrl = websiteUrl && /^https?:\/\//i.test(websiteUrl)
    ? websiteUrl
    : `https://${websiteUrl}`;
  const hasPreviousAccount = Boolean(normalizedUsername || profile?.ig_name);

  if (!isConnected && !canManageConnection) return null;

  return (
    <section className={styles.instagramCard} aria-labelledby="instagram-profile-title">
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <span className={styles.icon} aria-hidden="true"><Instagram size={20} /></span>
          <div>
            <p className={styles.eyebrow}>{t('instagram.social_presence')}</p>
            <h2 id="instagram-profile-title" className={styles.title}>Instagram</h2>
          </div>
        </div>
        {isConnected && (
          <span className={styles.connectedBadge}>
            <CheckCircle2 size={14} aria-hidden="true" /> {t('instagram.connected')}
          </span>
        )}
      </div>

      {isConnected ? (
        <div className={styles.accountContent}>
          <div className={styles.identityRow}>
            {profilePictureIsFromInstagram && (
              <img
                src={profile.ig_profile_picture_url}
                alt={t('instagram.profile_picture_alt')}
                className={styles.profilePicture}
              />
            )}
            <div className={styles.identityCopy}>
              {normalizedUsername ? (
                <a
                  href={`https://instagram.com/${normalizedUsername}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.handle}
                >
                  @{normalizedUsername} <ExternalLink size={14} aria-hidden="true" />
                </a>
              ) : (
                <strong className={styles.handle}>{profile.ig_name || t('instagram.professional_account')}</strong>
              )}
              {profile.ig_name && normalizedUsername && <span className={styles.accountName}>{profile.ig_name}</span>}
              <span className={styles.accountType}>{t('instagram.professional_account')}</span>
            </div>
          </div>

          {profile.ig_biography && <p className={styles.biography}>{profile.ig_biography}</p>}

          <div className={styles.footerRow}>
            <span className={styles.synced}>{t('instagram.synced')}</span>
            {websiteUrl && (
              <a href={normalizedWebsiteUrl} target="_blank" rel="noopener noreferrer" className={styles.website}>
                {t('instagram.website')} <ExternalLink size={13} aria-hidden="true" />
              </a>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.disconnectedState}>
          <div>
            <strong>
              {hasPreviousAccount ? t('instagram.needs_attention') : t('instagram.connect_prompt')}
            </strong>
            {hasPreviousAccount && normalizedUsername && <span>@{normalizedUsername}</span>}
          </div>
          <button type="button" onClick={onConnect}>
            {hasPreviousAccount ? t('instagram.reconnect') : t('instagram.connect')}
          </button>
        </div>
      )}
    </section>
  );
};

export default InstagramBasicProfileData;
